import {ChangeDetectionStrategy, Component, Input, SimpleChanges} from '@angular/core';
import {AladinSkyLayersService} from "../aladin-sky-layers.service";
import {SkyLayer} from "../aladin-sky-layers-options/aladin-sky-layers-options.component";
import {Observable, tap} from "rxjs";

const displayOptionsOnTopStyle =  'position: absolute; top: 10px';
const displayOptionsOnBottomStyle =  'position: absolute; bottom: 10px';
const displayOptionsRightStyle =  'position: absolute; right: 10px; top: 20px';

@Component({
  selector: 'asax-sky-view-aladin-sky-layers-container',
  templateUrl: './aladin-sky-layers-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AladinSkyLayersContainerComponent {
  @Input() height: number;
  @Input() width: number;

  skyLayers: Observable<SkyLayer[]>;

  baseImageForProgress: SkyLayer;
  overlayImageForProgress: SkyLayer;
  alphaForProgress: number;

  indexActiveSky: number;
  numberOfSkyLayers: number = 0;
  maxWidthForSkyOptions: number = 470;
  maxHeightForSkyOptions: number = 315;

  constructor(private readonly aladinLayersService: AladinSkyLayersService) {
    this.skyLayers = this.aladinLayersService.getSkyLayers();

    this.aladinLayersService.getSkyLayers().pipe(
      tap((skyLayers: SkyLayer[]) => this.numberOfSkyLayers = skyLayers.length)
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.height || changes.width) {
      this.updatePopoverSettings();
    }
  }

  
  // Sets the placement and styling of the sky layers popover.
  setPlacement(placement: 'right' | 'bottom' | 'top', style: any): void {
      this.aladinLayersService.setSkyLayersPlacement(placement);
      this.aladinLayersService.setSkyLayersStyle(style);
  };

  updatePopoverSettings(): void {
    const entireWindowHeight = window.innerHeight;
    const skyViewWidth = this.width;
    const skyViewHeight = this.height;
    const headerHeight = 30;
    const topSpaceHeight = skyViewHeight + headerHeight;
    const bottomSpaceHeight = entireWindowHeight - topSpaceHeight;
    const totalHeightOfTheSkyLayer = this.getTotalHeightOfTheSkyLayers();
    const halfOfTheTotalHeightOfTheSkyLayers = totalHeightOfTheSkyLayer / 2;

    // Default maximum dimensions for the sky options
    this.maxWidthForSkyOptions = 500;
    this.maxHeightForSkyOptions = 315;

    /**
     * Determines whether the popover height should be reduced
     */
    const shouldReduceHeight = (space: number) => space < halfOfTheTotalHeightOfTheSkyLayers;

    // If sky view width is smaller than maxWidthForSkyOptions, prioritize right-side placement
    if (skyViewWidth < 500) {
      /**
        If there is not enough space to display it in the right side 
          decrease the total height of the sky layers options component
      */
      if (shouldReduceHeight(skyViewHeight) || shouldReduceHeight(bottomSpaceHeight)) {
        this.maxHeightForSkyOptions = Math.min(skyViewHeight, bottomSpaceHeight);
      }
      this.setPlacement('right', displayOptionsRightStyle);
      return;
    }

    // If there's enough space below, use bottom placement
    if (bottomSpaceHeight > totalHeightOfTheSkyLayer) {
      this.setPlacement('bottom', displayOptionsOnBottomStyle);
      return;
    }

    // If there's enough space above, use top placement
    if (topSpaceHeight > totalHeightOfTheSkyLayer) {
      this.setPlacement('top', displayOptionsOnTopStyle);
      return;
    }

    /**
      If there is not enough space to display it in the right side 
        decrease the total height of the sky layers options component
    */
    if (shouldReduceHeight(skyViewHeight) || shouldReduceHeight(bottomSpaceHeight)) {
      this.maxHeightForSkyOptions = Math.min(skyViewHeight, bottomSpaceHeight);
    }
    this.setPlacement('right', displayOptionsRightStyle);
  }

  getTotalHeightOfTheSkyLayers(): number {
    const footerHeightOfTheSkyLayers = 41;

    const displayedOptionsHeight = 45 * this.numberOfSkyLayers;
    return footerHeightOfTheSkyLayers + displayedOptionsHeight;
  }

  onOutSkyLayers(skyLayers: SkyLayer[]): void {
    this.aladinLayersService.setSkyLayers(skyLayers);
  }

  onBaseImageForAladin(baseImage: SkyLayer): void {
    this.aladinLayersService.setBaseSky(baseImage);
  }

  onOverlayImageForAladin(overlayImage: SkyLayer): void {
    this.aladinLayersService.setOverlaySky(overlayImage);
  }

  onAlphaForAladin(alpha: number): void {
    this.aladinLayersService.setAlpha(alpha);
  }
}
